/**
 * The house style of Heimdall's Word reports: a title page with the logo, a
 * contents page Word fills in, headed and footed body pages, and the tables,
 * pills and banners results are shown with. The playbook report and the
 * refresh proposal are both built from it, so they read as one family.
 *
 * Builders take the docx module as loaded by the caller, so the library is
 * fetched only when somebody exports.
 */

type Docx = typeof import("docx");

// A navy and blue for structure, and the usual green, amber and red for
// results, each with a pale fill for shaded cells.
export const C = {
  brand: "0F3D66", accent: "1D6FD8", ink: "1F2937", muted: "6B7280", rule: "C9D6E8", band: "EAF1FB", zebra: "F6F8FB",
  ok: "15803D", okBg: "DCFCE7", warn: "B45309", warnBg: "FEF3C7", bad: "B91C1C", badBg: "FEE2E2", info: "1D4ED8", infoBg: "DBEAFE", none: "4B5563", noneBg: "F3F4F6",
};
export type Shade = "ok" | "warn" | "bad" | "info" | "none";
export const SHADE: Record<Shade, [string, string]> = { ok: [C.ok, C.okBg], warn: [C.warn, C.warnBg], bad: [C.bad, C.badBg], info: [C.info, C.infoBg], none: [C.none, C.noneBg] };

export type Logo = { data: Uint8Array; type: "jpg" | "png"; width: number; height: number } | null;

/** The product logo for the title page, when the console can fetch it. */
export async function fetchLogo(): Promise<Logo> {
  try {
    const res = await fetch("/assets/heimdall-logo.jpg");
    if (!res.ok) return null;
    return { data: new Uint8Array(await res.arrayBuffer()), type: "jpg", width: 1408, height: 768 };
  } catch {
    return null;
  }
}

/** Hands a built file to the browser as a download. */
export function saveBlob(blob: Blob, fileName: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

export function when(iso: string): string {
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "medium" });
}
export function day(iso: string): string {
  return new Date(iso).toLocaleDateString([], { dateStyle: "long" });
}

export type CellSpec = string | { text: string; shade?: Shade; mono?: boolean; bold?: boolean; lines?: { text: string; color?: string; italics?: boolean }[] };
type RunOpts = { bold?: boolean; italics?: boolean; color?: string; size?: number; font?: string };

/** The building blocks of a report body. */
export function blocks(d: Docx) {
  const { Paragraph, TextRun, Table, TableRow, TableCell, WidthType, ShadingType, BorderStyle, VerticalAlign } = d;
  const text = (t: string, o: RunOpts = {}) => new TextRun({ text: t, ...o });
  const para = (t: string, o: RunOpts & { after?: number; align?: (typeof d.AlignmentType)[keyof typeof d.AlignmentType] } = {}) =>
    new Paragraph({ children: [text(t, o)], spacing: { after: o.after ?? 120 }, alignment: o.align });
  const muted = (t: string) => para(t, { italics: true, color: C.muted, size: 18 });
  const heading = (t: string, level: (typeof d.HeadingLevel)[keyof typeof d.HeadingLevel], pageBreakBefore = false) => new Paragraph({ text: t, heading: level, pageBreakBefore });
  /** A coloured word with a pale fill, like a pill. */
  const pill = (t: string, s: Shade) => new TextRun({ text: ` ${t} `, bold: true, size: 18, color: SHADE[s][0], shading: { type: ShadingType.CLEAR, fill: SHADE[s][1], color: "auto" } });
  /** A thin rule between sections. */
  const divider = () => new Paragraph({ children: [], spacing: { before: 120, after: 120 }, border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: C.rule, space: 1 } } });
  const thin = { style: BorderStyle.SINGLE, size: 4, color: C.rule };
  const none = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const cell = (spec: CellSpec, kind: "head" | "key" | "body", zebra = false) => {
    const c = typeof spec === "string" ? { text: spec } : spec;
    const shade = c.shade ? SHADE[c.shade] : null;
    const fill = kind === "head" ? C.brand : kind === "key" ? C.band : shade ? shade[1] : zebra ? C.zebra : undefined;
    return new TableCell({
      verticalAlign: VerticalAlign.CENTER,
      margins: { top: 50, bottom: 50, left: 90, right: 90 },
      ...(fill ? { shading: { type: ShadingType.CLEAR, fill, color: "auto" } } : {}),
      children: c.lines ? c.lines.map((l) => new Paragraph({ children: [new TextRun({ text: l.text || " ", size: 17, font: "Consolas", color: l.color ?? C.ink, italics: l.italics })] })) : (c.text || " ").split("\n").map((line) => new Paragraph({ children: [new TextRun({
        text: line, size: 18, bold: kind !== "body" || c.bold || !!shade, font: c.mono ? "Consolas" : undefined,
        color: kind === "head" ? "FFFFFF" : kind === "key" ? C.brand : shade ? shade[0] : C.ink,
      })] })),
    });
  };
  const borders = { top: thin, bottom: thin, left: none, right: none, insideHorizontal: thin, insideVertical: none };
  const table = (head: string[], rows: CellSpec[][]) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE }, borders,
      rows: [
        new TableRow({ tableHeader: true, children: head.map((h) => cell(h, "head")) }),
        ...rows.map((r, i) => new TableRow({ cantSplit: true, children: r.map((c) => cell(c, "body", i % 2 === 1)) })),
      ],
    });
  const pairs = (rows: [string, CellSpec][]) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE }, borders,
      columnWidths: [2600, 6400],
      rows: rows.map(([k, v]) => new TableRow({ cantSplit: true, children: [cell(k, "key"), cell(v, "body")] })),
    });
  /** A full-width shaded band with a coloured edge: a result at a glance. */
  const banner = (runs: InstanceType<typeof TextRun>[], s: Shade) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: { top: none, bottom: none, right: none, insideHorizontal: none, insideVertical: none, left: { style: BorderStyle.SINGLE, size: 36, color: SHADE[s][0] } },
      rows: [new TableRow({ children: [new TableCell({ shading: { type: ShadingType.CLEAR, fill: SHADE[s][1], color: "auto" }, margins: { top: 120, bottom: 120, left: 200, right: 200 }, children: [new Paragraph({ children: runs })] })] })],
    });
  const spacer = (after = 120) => new Paragraph({ children: [], spacing: { after } });
  /** Monospaced text, such as a configuration, one paragraph per line. */
  const code = (t: string) => t.replace(/\n$/, "").split("\n").map((line) => new Paragraph({ spacing: { after: 0 }, children: [new TextRun({ text: line || " ", font: "Consolas", size: 16, color: C.ink })] }));
  return { text, para, muted, heading, pill, divider, cell, table, pairs, banner, spacer, code };
}

type Block = InstanceType<Docx["Paragraph"]> | InstanceType<Docx["Table"]> | InstanceType<Docx["TableOfContents"]>;

/**
 * A whole report: title page, contents and body, in the house style.
 * `kind` is what the document is ("Network validation report").
 */
export function assemble(d: Docx, o: {
  title: string; kind: string; description?: string; logo: Logo;
  facts: [string, CellSpec][]; body: Block[];
}) {
  const { Document, Paragraph, ImageRun, AlignmentType, BorderStyle, Header, Footer, PageNumber, TableOfContents, TabStopType, TabStopPosition, TextRun } = d;
  const { text, para, muted, pairs } = blocks(d);
  const cover: Block[] = [new Paragraph({ children: [], spacing: { before: 1400 } })];
  if (o.logo) {
    const w = 330, h = Math.round((w * o.logo.height) / o.logo.width);
    cover.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 360 }, children: [new ImageRun({ type: o.logo.type, data: o.logo.data, transformation: { width: w, height: h } })] }));
  }
  cover.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 120 }, children: [text(o.title, { bold: true, size: 60, color: C.brand })] }));
  cover.push(new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 240 }, children: [text(o.kind, { size: 32, color: C.accent })] }));
  if (o.description) cover.push(para(o.description, { align: AlignmentType.CENTER, color: C.muted, italics: true, after: 360 }));
  cover.push(new Paragraph({ children: [], spacing: { after: 360 }, border: { bottom: { style: BorderStyle.SINGLE, size: 18, color: C.accent, space: 1 } } }));
  cover.push(pairs(o.facts));

  const contents: Block[] = [
    new Paragraph({ children: [text("Contents", { bold: true, size: 40, color: C.brand })], spacing: { after: 240 } }),
    new TableOfContents("Contents", { hyperlink: true, headingStyleRange: "1-2" }),
    muted("Word fills in this table when the document opens. If page numbers are missing, right-click it and choose Update Field."),
  ];

  const generated = when(new Date().toISOString());
  const header = new Header({ children: [new Paragraph({
    tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: C.rule, space: 4 } },
    children: [text(o.title, { bold: true, color: C.brand, size: 16 }), text(`\t${o.kind}`, { color: C.muted, size: 16 })],
  })] });
  const footer = new Footer({ children: [new Paragraph({
    tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
    children: [text(`Generated ${generated}`, { color: C.muted, size: 16 }), new TextRun({ children: ["\tPage ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES], color: C.muted, size: 16 })],
  })] });
  // US Letter, the size this deployment's readers print on.
  const margins = { page: { size: { width: 12240, height: 15840 }, margin: { top: 1200, bottom: 1100, left: 1100, right: 1100 } } };
  return new Document({
    creator: "Heimdall",
    title: `${o.title} — ${o.kind.toLowerCase()}`,
    description: o.description || undefined,
    features: { updateFields: true },
    styles: {
      default: {
        document: { run: { font: "Calibri", size: 21, color: C.ink } },
        title: { run: { font: "Calibri", size: 56, bold: true, color: C.brand } },
        heading1: { run: { font: "Calibri", size: 34, bold: true, color: C.brand }, paragraph: { spacing: { before: 240, after: 160 }, border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: C.accent, space: 4 } } } },
        heading2: { run: { font: "Calibri", size: 26, bold: true, color: C.brand }, paragraph: { spacing: { before: 200, after: 120 } } },
        heading3: { run: { font: "Calibri", size: 22, bold: true, color: C.accent }, paragraph: { spacing: { before: 160, after: 80 } } },
      },
    },
    sections: [
      { properties: margins, children: cover },
      { properties: margins, children: contents },
      { properties: margins, headers: { default: header }, footers: { default: footer }, children: o.body },
    ],
  });
}
